import classNames from 'classnames';
import React, {useMemo} from 'react';

import {TrackImpression} from '@udemy/event-tracking';
import {Icon} from '@udemy/icons';
import {
    VerticalCard,
    CardTitle,
    CardMediaImage,
    TagList,
    Tag,
    type CardMediaImageProps,
} from '@udemy/react-product-card-components';
import {TextSkeleton} from '@udemy/react-reveal-components';
import {noop} from '@udemy/shared-utils';

import {useEnrollmentDisplay} from 'src/hooks/useEnrollmentDisplay';

import styles from './cert-card.module.less';

interface CertCardProps {
    /** Certification title */
    title?: string;
    /**
     * Wide media image for the card. Rendered with `horizontal` layout.
     *
     * @remarks
     *
     * Use a `wide` layout of `CardMediaImage`.
     *
     * @see `CardMediaImage` component
     **/
    cardMediaProps: Omit<CardMediaImageProps, 'layout'>;
    /** Descriptions for the certification card. appears in secondaryContent area */
    description?: React.ReactNode;
    /** Number of enrollments for the certification card. appears in secondaryContent area */
    num_enrollments?: number;
    /** URL to navigate to when the card is clicked */
    href?: string;
    /** Function to call when the card is clicked */
    onClick?: () => void;
    /** Function to call when the card is tracked */
    onImpression?: () => void;
}

export const CertCardVertical = ({
    title,
    description,
    num_enrollments,
    cardMediaProps,
    href,
    onClick,
    onImpression = noop,
}: CertCardProps) => {
    const {renderEnrollments} = useEnrollmentDisplay();

    const renderTitle = (title: string | undefined) => {
        if (title) {
            return (
                <CardTitle
                    renderLink={CardTitle.renderAnchorTag({href, onClick})}
                    headingLevel={'h3'}
                >
                    {title}
                </CardTitle>
            );
        }
        return (
            <TextSkeleton
                className={classNames(styles.title, styles['title-loading'], 'ud-text-lg')}
                lineCountPerParagraph={1}
                paragraphCount={1}
            />
        );
    };

    const tags = useMemo(() => {
        if (!num_enrollments) {
            return null;
        }

        return (
            <TagList
                tags={[
                    <Tag key="tag1">
                        <Icon glyph="people" color="inherit" label={false} />{' '}
                        {renderEnrollments(num_enrollments)}
                    </Tag>,
                ]}
            />
        );
    }, [num_enrollments, renderEnrollments]);

    return (
        <>
            <TrackImpression trackFunc={onImpression}>
                <div>
                    <VerticalCard
                        className={styles['cert-card']}
                        title={renderTitle(title)}
                        media={<CardMediaImage {...cardMediaProps} layout="wide" />}
                        description={
                            <p className={classNames('ud-text-xs', styles.description)}>
                                {description}
                            </p>
                        }
                        tags={tags}
                        showSecondaryContentBorder={false}
                    />
                </div>
            </TrackImpression>
        </>
    );
};
